import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Broadcast, EyeSlash, Warning } from "@phosphor-icons/react";

const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const fmtT = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" }).replace(":", "");
const fmtD = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "America/New_York" });

function HealthTags({ w }) {
  return (
    <>
      {w.missing_count > 0 && <Badge className="rounded-sm text-[10px] bg-red-600 text-white" data-testid={`pubw-missing-${w.week_start}`}><Warning size={10} className="mr-1" />{w.missing_count} missing</Badge>}
      {w.extra_count > 0 && <Badge className="rounded-sm text-[10px] bg-orange-500 text-white" data-testid={`pubw-extra-${w.week_start}`}><Warning size={10} className="mr-1" />{w.extra_count} extra</Badge>}
      {w.total === 0 && <Badge className="rounded-sm text-[10px] bg-slate-400 text-white">No shifts</Badge>}
    </>
  );
}

function WeekPickRow({ w, checked, viewed, onToggle }) {
  return (
    <label className={`flex items-center gap-2 px-2 py-1.5 border rounded-sm text-xs cursor-pointer ${checked ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid={`pubw-row-${w.week_start}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} disabled={w.total === 0} data-testid={`pubw-check-${w.week_start}`} />
      <span className="mono font-semibold">{fmtDate(w.week_start)} – {fmtDate(w.week_end)}</span>
      {viewed && <Badge className="rounded-sm text-[10px] bg-blue-700 text-white">Viewing</Badge>}
      <span className="text-slate-600 mono" data-testid={`pubw-counts-${w.week_start}`}>{w.draft_count} draft · {w.published_count} published{w.assigned_count ? ` · ${w.assigned_count} assigned` : ""}</span>
      <span className="ml-auto flex gap-1"><HealthTags w={w} /></span>
    </label>
  );
}

function ShiftTable({ tours, testPrefix }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 sticky top-0">
        <tr><th className="px-3 py-1.5 text-left">Date</th><th className="px-3 py-1.5 text-left">Time</th><th className="px-3 py-1.5 text-left">Position</th><th className="px-3 py-1.5 text-left">Role</th><th className="px-3 py-1.5 text-left">Assignee</th></tr>
      </thead>
      <tbody>
        {tours.map((t) => (
          <tr key={t.id} className="border-b border-slate-100" data-testid={`${testPrefix}-${t.id}`}>
            <td className="px-3 py-1.5 mono">{fmtD(t.start_time)}</td>
            <td className="px-3 py-1.5 mono">{fmtT(t.start_time)}–{fmtT(t.end_time)}</td>
            <td className="px-3 py-1.5">{t.position || t.title}</td>
            <td className="px-3 py-1.5">{t.ambulance_role || "—"}</td>
            <td className="px-3 py-1.5">{t.assigned_user_name || <span className="text-amber-700">Unassigned</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ViewedWeekPreview({ tours, weekLabel }) {
  const willPublish = tours.filter((t) => t.published === false);
  const alreadyLive = tours.filter((t) => t.published !== false);
  if (!tours.length) return null;
  return (
    <details className="border border-slate-200 rounded-sm" data-testid="publish-viewed-week-preview">
      <summary className="text-xs px-3 py-2 cursor-pointer text-slate-700 bg-slate-50 hover:bg-slate-100">
        Preview the week you're viewing ({weekLabel}): <strong>{willPublish.length} draft</strong> · {alreadyLive.length} already published
      </summary>
      <div className="max-h-64 overflow-y-auto"><ShiftTable tours={willPublish.length ? willPublish : alreadyLive} testPrefix="preview-will" /></div>
    </details>
  );
}

function ResultsPanel({ results }) {
  return (
    <div className="border border-slate-200 rounded-sm p-2 text-xs" data-testid="pubw-results">
      <div className="font-semibold mb-1" data-testid="pubw-results-summary">
        {results.published ? "Published" : "Unpublished"} {results.updated} shift(s) · {results.weeks.length - results.weeks_failed} of {results.weeks.length} week(s) updated
        {results.published && <span className="font-normal text-slate-600" data-testid="pubw-notified"> · {results.notified ? `inbox alert sent to ${results.notified} member(s)` : "no alert sent (nothing newly published)"}</span>}
      </div>
      {results.weeks.map((w) => (
        <div key={w.week_start} className={`flex justify-between px-2 py-1 mb-1 rounded-sm border ${w.status === "ok" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid={`pubw-result-${w.week_start}`}>
          <span className="mono">{fmtDate(w.week_start)}{w.week_end ? ` – ${fmtDate(w.week_end)}` : ""}</span>
          {w.status === "ok" ? <span className="text-emerald-700">{w.updated} shift(s) {results.published ? "published" : "unpublished"}</span> : <span className="text-red-700">Failed · {w.error}</span>}
        </div>
      ))}
    </div>
  );
}

export default function PublishWeeksDialog({ open, viewedWeekStart, viewedTours, weekLabel, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [includePublished, setIncludePublished] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [confirmUnpub, setConfirmUnpub] = useState(false);

  async function loadRows(incl = includePublished) {
    try {
      const { data } = await api.get(`/schedule-health/publish-overview?weeks=52&include_published=${incl}&always_include=${viewedWeekStart}`, { timeout: 120000 });
      setRows(data.weeks);
      return data.weeks;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load weeks"); return []; }
  }

  useEffect(() => {
    if (!open) return;
    setResults(null); setIncludePublished(false);
    setSelected(new Set(viewedTours.length ? [viewedWeekStart] : []));
    loadRows(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewedWeekStart]);

  const selectable = rows.filter((w) => w.total > 0);
  const allChecked = selectable.length > 0 && selectable.every((w) => selected.has(w.week_start));
  const toggle = (ws) => setSelected((prev) => { const n = new Set(prev); n.has(ws) ? n.delete(ws) : n.add(ws); return n; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(selectable.map((w) => w.week_start)));
  const flagged = rows.filter((w) => selected.has(w.week_start) && (w.missing_count || w.extra_count)).length;

  async function run(published) {
    setConfirmUnpub(false); setBusy(true);
    try {
      const { data } = await api.post("/schedule-health/publish-weeks", { week_starts: [...selected], published }, { timeout: 120000 });
      setResults(data);
      if (data.weeks_failed) toast.error(`${data.weeks_failed} week(s) failed — see details`);
      else toast.success(`${published ? "Published" : "Unpublished"} ${data.updated} shift(s) across ${data.weeks.length} week(s)${data.notified ? ` · ${data.notified} member(s) notified` : ""}`);
      const fresh = await loadRows();
      setSelected((prev) => new Set([...prev].filter((ws) => fresh.some((w) => w.week_start === ws))));
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="publish-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Broadcast size={18} weight="fill" /> Publish Weeks</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Select the weeks to release to volunteers. Only the published/draft state changes — assignments are never touched.</p>
        </DialogHeader>
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1 font-semibold cursor-pointer"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="pubw-select-all" /> Select all ({selectable.length})</label>
          <label className="flex items-center gap-1 cursor-pointer text-slate-600"><input type="checkbox" checked={includePublished} onChange={(e) => { setIncludePublished(e.target.checked); loadRows(e.target.checked); }} data-testid="pubw-include-published" /> Show fully published weeks</label>
          <span className="ml-auto mono" data-testid="pubw-selected-count">{selected.size} selected</span>
        </div>
        <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="pubw-list">
          {rows.length === 0 && <div className="text-xs text-slate-500 italic p-3 border border-dashed border-slate-200 rounded-sm">No upcoming weeks with draft shifts.</div>}
          {rows.map((w) => <WeekPickRow key={w.week_start} w={w} checked={selected.has(w.week_start)} viewed={w.week_start === viewedWeekStart} onToggle={() => toggle(w.week_start)} />)}
        </div>
        {flagged > 0 && <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-sm px-2 py-1" data-testid="pubw-health-warning">{flagged} selected week(s) don't match the baseline template (see tags). You can still publish.</div>}
        <ViewedWeekPreview tours={viewedTours} weekLabel={weekLabel} />
        {results && <ResultsPanel results={results} />}
        <DialogFooter className="pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="publish-preview-cancel">Close</Button>
          <Button variant="outline" disabled={busy || !selected.size} onClick={() => setConfirmUnpub(true)} className="rounded-sm border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="publish-preview-unpublish"><EyeSlash size={14} className="mr-1" /> Unpublish selected</Button>
          <Button disabled={busy || !selected.size} onClick={() => run(true)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="publish-preview-confirm"><Broadcast size={14} className="mr-1" /> {busy ? "Working…" : `Publish selected (${selected.size})`}</Button>
        </DialogFooter>
        <AlertDialog open={confirmUnpub} onOpenChange={setConfirmUnpub}>
          <AlertDialogContent data-testid="pubw-unpublish-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Unpublish {selected.size} week(s)?</AlertDialogTitle>
              <AlertDialogDescription>Volunteers will no longer see these shifts until they are published again. Assignments are kept.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="pubw-unpublish-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => run(false)} className="bg-amber-600 hover:bg-amber-700" data-testid="pubw-unpublish-confirm-btn">Unpublish</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
